/**
 * Shared derivation layer for the simulated QuickBooks screens.
 *
 * Everything money-shaped in the demo starts here, so Financials, Budget,
 * Labor and Collections cannot disagree with each other or with the tables the
 * rest of the app reads:
 *
 *   • Revenue comes from the `job_invoices` snapshot — real invoice subtotals,
 *     bucketed by the month they were invoiced and by division.
 *   • The chart of accounts and every budget goal come from the REAL file
 *     `public/data/budget-2026.json` (the same file the production
 *     /api/qb-budget Pages Function reads). No account name is invented.
 *   • Cost and expense ACTUALS are generated: each account's share of budgeted
 *     revenue is applied to actual revenue, with a small deterministic jitter
 *     so the P&L doesn't read as one flat ratio.
 *   • The cash-vs-accrual factor is derived from `v_jobs_full`
 *     (Σ total_collected ÷ Σ total_invoiced).
 */
import { loadTable } from '~/demo/data';
import { jitter, once } from './prng';

export type Row = Record<string, unknown>;

export const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};
export const str = (v: unknown): string => (v == null ? '' : String(v));
export const round2 = (n: number): number => Math.round(n * 100) / 100;

export const NY_TZ = 'America/New_York';

/** YYYY-MM-DD for a Date, in New York (the company's timezone). */
export function nyDate(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: NY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

/** Minutes past midnight in New York. */
export function nyMinutes(d: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NY_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return h * 60 + m;
}

/** Whole calendar days between two ISO timestamps (never negative). */
export function calendarDaysSince(iso: string, now: Date = new Date()): number {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 0;
  const a = Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate());
  const b = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

// ── Revenue, straight from the snapshot ──────────────────────────────────────

export interface RevenueModel {
  /** 'YYYY-MM' → Σ job_invoices.invoice_subtotal. */
  byMonth: Map<string, number>;
  /** 'YYYY-MM' → division code → Σ invoice_subtotal. */
  byMonthDivision: Map<string, Map<string, number>>;
  /** Σ total_collected ÷ Σ total_invoiced across v_jobs_full — the cash factor. */
  cashFactor: number;
  /** Division code → label, from the `divisions` table. */
  divisionLabels: Map<string, string>;
}

export const revenueModel = once(async (): Promise<RevenueModel> => {
  const [invoices, jobs, divisions] = await Promise.all([
    loadTable('job_invoices'), loadTable('v_jobs_full'), loadTable('divisions'),
  ]);

  const byMonth = new Map<string, number>();
  const byMonthDivision = new Map<string, Map<string, number>>();
  for (const r of invoices) {
    const key = str(r.invoiced_at).slice(0, 7);
    if (key.length !== 7) continue;
    const amount = num(r.invoice_subtotal);
    byMonth.set(key, (byMonth.get(key) ?? 0) + amount);
    const div = str(r.division) || 'WTR';
    const inner = byMonthDivision.get(key) ?? new Map<string, number>();
    inner.set(div, (inner.get(div) ?? 0) + amount);
    byMonthDivision.set(key, inner);
  }

  let invoiced = 0;
  let collected = 0;
  for (const j of jobs) {
    invoiced += num(j.total_invoiced);
    collected += num(j.total_collected);
  }
  const cashFactor = invoiced > 0 ? Math.min(1, collected / invoiced) : 0.88;

  const divisionLabels = new Map<string, string>();
  for (const d of divisions) divisionLabels.set(str(d.code), str(d.label) || str(d.code));

  return { byMonth, byMonthDivision, cashFactor, divisionLabels };
});

/** Inclusive list of 'YYYY-MM' keys between two months. */
export function monthRange(fromKey: string, toKey: string): string[] {
  const out: string[] = [];
  let [y, m] = fromKey.split('-').map(Number);
  for (let guard = 0; guard < 240; guard++) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    out.push(key);
    if (key >= toKey) break;
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

/** Σ invoice subtotals over a month range, and the same split by division. */
export function revenueOver(model: RevenueModel, months: string[]): {
  total: number; byDivision: Map<string, number>;
} {
  let total = 0;
  const byDivision = new Map<string, number>();
  for (const key of months) {
    total += model.byMonth.get(key) ?? 0;
    for (const [div, amt] of model.byMonthDivision.get(key) ?? []) {
      byDivision.set(div, (byDivision.get(div) ?? 0) + amt);
    }
  }
  return { total, byDivision };
}

// ── The real budget file: chart of accounts + goals ──────────────────────────

export interface BudgetDoc {
  fyStart: string;
  fyEnd: string;
  budgetName: string;
  /** Account name (e.g. "4101 Water Mitigation") → 'YYYY-MM' → budgeted amount. */
  accounts: Record<string, Record<string, number>>;
}

const EMPTY_BUDGET: BudgetDoc = {
  fyStart: `${new Date().getFullYear()}-01-01`,
  fyEnd: `${new Date().getFullYear()}-12-31`,
  budgetName: 'Budget (file unavailable)',
  accounts: {},
};

/**
 * The static budget the production endpoint also reads. Fetched rather than
 * imported so the 29 KB of JSON stays out of the app bundle and out of `tsc`'s
 * literal-type inference.
 */
export const budgetDoc = once(async (): Promise<BudgetDoc> => {
  try {
    const res = await fetch('/data/budget-2026.json');
    if (!res.ok) return EMPTY_BUDGET;
    const json = (await res.json()) as Partial<BudgetDoc> | null;
    if (!json || typeof json.accounts !== 'object' || !json.accounts) return EMPTY_BUDGET;
    return {
      fyStart: json.fyStart ?? EMPTY_BUDGET.fyStart,
      fyEnd: json.fyEnd ?? EMPTY_BUDGET.fyEnd,
      budgetName: json.budgetName ?? 'Budget',
      accounts: json.accounts,
    };
  } catch {
    return EMPTY_BUDGET;
  }
});

export const accountNumber = (name: string): string => name.trim().split(/\s+/)[0] ?? '';
export const isIncomeAccount = (name: string): boolean => accountNumber(name).startsWith('4');
export const isCogsAccount = (name: string): boolean => accountNumber(name).startsWith('5');
export const isExpenseAccount = (name: string): boolean =>
  /^[6789]/.test(accountNumber(name));

/**
 * Division code → the income account it lands in. Numbers only; the display
 * name always comes from the budget file so nothing is transcribed by hand.
 * Divisions with no obvious account fall back to Rebuild Revenue.
 */
const DIVISION_ACCOUNT: Record<string, string> = {
  WTR: '4101', MLD: '4163', STC: '4201', TRM: '4301', ENV: '4301',
  CON: '4402', CPO: '4402', ASB: '4502', REF: '4600', REC: '4005', TMP: '4005',
};
/** Sales-discount account → the income account it discounts (from the file). */
const DISCOUNT_PARENT: Record<string, string> = {
  '4105': '4101', '4164': '4163', '4205': '4201', '4305': '4301', '4405': '4402',
};

export interface PnlLine {
  name: string;
  /** '4101', '5101', … */
  number: string;
  actual: number;
  budget: number;
}
export interface PnlAccounts {
  income: PnlLine[];
  cogs: PnlLine[];
  expenses: PnlLine[];
  totals: { income: number; cogs: number; expenses: number };
  /** The negative sales-discount lines, i.e. the write-downs. */
  discounts: PnlLine[];
  grossInvoiced: number;
}

function budgetSum(doc: BudgetDoc, name: string, months: string[]): number {
  const row = doc.accounts[name];
  if (!row) return 0;
  let total = 0;
  for (const m of months) total += num(row[m]);
  return total;
}

/** Full-fiscal-year budgeted net income (income accounts, discounts included). */
function fyIncomeBudget(doc: BudgetDoc, fyMonths: string[]): number {
  let total = 0;
  for (const name of Object.keys(doc.accounts)) {
    if (isIncomeAccount(name)) total += budgetSum(doc, name, fyMonths);
  }
  return total;
}

/**
 * Build a period's P&L.
 *
 *   income   — real: actual invoice subtotals for the period, allocated to the
 *              budget file's income accounts by division.
 *   discount — generated: each "Sales Discount" account keeps the ratio the
 *              budget file gives it against its parent income account.
 *   cost     — generated: the account's share of budgeted FY revenue applied to
 *              this period's actual revenue, ±12% deterministic jitter.
 */
export async function pnlAccountsFor(
  months: string[],
  seedKey: string,
  opts: { cash?: boolean } = {},
): Promise<PnlAccounts> {
  const [model, doc] = await Promise.all([revenueModel(), budgetDoc()]);
  const fyMonths = monthRange(doc.fyStart.slice(0, 7), doc.fyEnd.slice(0, 7));
  const { total: grossRevenueRaw, byDivision } = revenueOver(model, months);
  const factor = opts.cash ? model.cashFactor : 1;
  const grossRevenue = grossRevenueRaw * factor;

  const names = Object.keys(doc.accounts);
  const byNumber = new Map<string, string>();
  for (const n of names) byNumber.set(accountNumber(n), n);

  // ── Income: real division revenue mapped onto the file's income accounts.
  const incomeByNumber = new Map<string, number>();
  for (const [div, amt] of byDivision) {
    const acct = DIVISION_ACCOUNT[div] ?? '4005';
    incomeByNumber.set(acct, (incomeByNumber.get(acct) ?? 0) + amt * factor);
  }

  const income: PnlLine[] = [];
  const discounts: PnlLine[] = [];
  for (const name of names) {
    if (!isIncomeAccount(name)) continue;
    const number = accountNumber(name);
    const parent = DISCOUNT_PARENT[number];
    if (parent) continue; // handled below, after its parent's actual is known
    const actual = incomeByNumber.get(number) ?? 0;
    if (actual === 0 && budgetSum(doc, name, months) === 0) continue;
    income.push({ name, number, actual: round2(actual), budget: round2(budgetSum(doc, name, months)) });
  }
  for (const [number, parent] of Object.entries(DISCOUNT_PARENT)) {
    const name = byNumber.get(number);
    const parentName = byNumber.get(parent);
    if (!name || !parentName) continue;
    const parentBudget = budgetSum(doc, parentName, fyMonths);
    const ratio = parentBudget !== 0 ? budgetSum(doc, name, fyMonths) / parentBudget : 0;
    const parentActual = incomeByNumber.get(parent) ?? 0;
    const actual = round2(parentActual * ratio);
    if (actual === 0) continue;
    const line: PnlLine = { name, number, actual, budget: round2(budgetSum(doc, name, months)) };
    discounts.push(line);
    income.push(line);
  }
  income.sort((a, b) => a.number.localeCompare(b.number));

  const totalIncome = income.reduce((s, l) => s + l.actual, 0);
  const grossInvoiced = grossRevenue;

  // ── Costs: budgeted share of FY revenue × this period's actual revenue.
  const fyIncome = fyIncomeBudget(doc, fyMonths) || 1;
  const cogs: PnlLine[] = [];
  const expenses: PnlLine[] = [];
  for (const name of names) {
    const number = accountNumber(name);
    const isCogs = isCogsAccount(name);
    const isExp = isExpenseAccount(name);
    if (!isCogs && !isExp) continue;
    const fyBudget = budgetSum(doc, name, fyMonths);
    if (fyBudget === 0) continue;
    const share = fyBudget / fyIncome;
    // Overheads track the calendar rather than revenue, so they lean on the
    // budgeted amount for the period; COGS scales with what we actually billed.
    const periodBudget = budgetSum(doc, name, months);
    const base = isCogs ? share * totalIncome : periodBudget;
    const actual = round2(Math.max(0, base * jitter(`${seedKey}|${number}`, 0.12)));
    const line: PnlLine = { name, number, actual, budget: round2(periodBudget) };
    (isCogs ? cogs : expenses).push(line);
  }
  cogs.sort((a, b) => a.number.localeCompare(b.number));
  expenses.sort((a, b) => a.number.localeCompare(b.number));

  return {
    income, cogs, expenses, discounts,
    grossInvoiced: round2(grossInvoiced),
    totals: {
      income: round2(totalIncome),
      cogs: round2(cogs.reduce((s, l) => s + l.actual, 0)),
      expenses: round2(expenses.reduce((s, l) => s + l.actual, 0)),
    },
  };
}

/** Budgeted totals for a month range, split the same three ways. */
export async function budgetTotalsFor(months: string[]): Promise<{
  income: number; cogs: number; expenses: number;
}> {
  const doc = await budgetDoc();
  let income = 0, cogs = 0, expenses = 0;
  for (const name of Object.keys(doc.accounts)) {
    const amt = budgetSum(doc, name, months);
    if (isIncomeAccount(name)) income += amt;
    else if (isCogsAccount(name)) cogs += amt;
    else if (isExpenseAccount(name)) expenses += amt;
  }
  return { income: round2(income), cogs: round2(cogs), expenses: round2(expenses) };
}

export { budgetSum };
